<template>
  <div>
    <p class="has-text-weight-bold is-size-4">{{ $t("mp:purchases.title") }}</p>

    <template v-if="loaded">
      <FieldListing
        :data="purchases"
        :columns="tableColumns"
        :actions="actions"
        :searchable="true"
        :paginated="true"
        default-sort="created"
        store-fetch-command="purchase/fetchList"
        store-get-command="purchase/getList"
        :search-fields="searchFields"
      >
        <template slot="empty">{{ $t("mp:purchases.table.empty") }}</template>
      </FieldListing>
    </template>

    <LoadingSkeleton v-else />
  </div>
</template>

<script>
import escapeHtml from "escape-html";
import LoadingSkeleton from "../components/LoadingSkeleton";
import FieldListing from "../components/FieldListing";
import {
  LOAN_APPLICATION_STATUS_TOKENS,
  PURCHASE_STATUS,
  SEARCH_FIELD_TYPES,
} from "../utils/const";
import i18n from "../plugins/i18n";
import { formatDateTime } from "../utils/util";
import { AUTHORITIES } from "../utils/authorities";

export default {
  name: "Purchases",
  title: "mp:page.purchases.title",
  components: {
    FieldListing,
    LoadingSkeleton,
  },
  async mounted() {
    await this.fetch();
    this.loaded = true;
  },
  data() {
    return {
      loaded: false,
      purchases: [],
      searchFields: [
        {
          name: "mp:purchases.list_header.id",
          identifier: "id",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: false,
        },
        {
          name: "mp:purchases.list_header.customer_name",
          identifier: "customerName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "mp:purchases.list_header.phone",
          identifier: "customerPhone",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "mp:purchases.list_header.purchase_status",
          identifier: "status",
          type: SEARCH_FIELD_TYPES.ENUM,
          values: PURCHASE_STATUS.TOKEN,
          genericSearch: false,
        },
        {
          name: "mp:purchases.list_header.application_status",
          identifier: "loanApplicationStatus",
          type: SEARCH_FIELD_TYPES.ENUM,
          values: LOAN_APPLICATION_STATUS_TOKENS,
          genericSearch: false,
        },
        {
          name: "mp:purchases.list_header.basket_amount",
          identifier: "totalAmount",
          type: SEARCH_FIELD_TYPES.INTEGER,
          genericSearch: false,
        },
        {
          name: "mp:purchases.list_header.created",
          identifier: "created",
          type: SEARCH_FIELD_TYPES.DATE,
          genericSearch: false,
        },
      ],
      tableColumns: [
        {
          field: "id",
          label: "mp:purchases.list_header.id",
          format: (row) => {
            return row.id;
          },
        },
        {
          field: "customerName",
          label: "mp:purchases.list_header.customer_name",
          vhtml: true,
          format: (row) => {
            return `<div>${escapeHtml(row.customerName)}
            <p class="is-size-7 has-text-grey-icon">${this.$options.filters.phone(
              escapeHtml(row.customerPhone)
            )}</p></div>`;
          },
        },
        {
          field: "status",
          label: "mp:purchases.list_header.purchase_status",
          vhtml: true,
          format: (row) => {
            return `<div>${i18n.t(PURCHASE_STATUS.TOKEN[row.status])}
            <p class="is-size-7 has-text-grey-icon">${this.$options.filters.empty(
              i18n.t(LOAN_APPLICATION_STATUS_TOKENS[row.loanApplicationStatus])
            )}</p></div>`;
          },
        },
        {
          field: "totalAmount",
          label: "mp:purchases.list_header.basket_amount",
          vhtml: true,
          format: (row) => {
            return `<div>${this.$options.filters.monify(row.totalAmount)} Ft
            <p class="is-size-7 has-text-grey-icon">${row.itemCount} ${i18n.t(
              "mp:purchases.basket_count"
            )}</p></div>`;
          },
        },
        {
          field: "created",
          label: "mp:purchases.list_header.created",
          vhtml: true,
          format: (row) => {
            return `<div>${escapeHtml(
              formatDateTime(row.created).split(" ")[1]
            )}
            <p class="is-size-7 has-text-grey-icon">${escapeHtml(
              formatDateTime(row.created).split(" ")[0]
            )}</p></div>`;
          },
        },
      ],
      actions: [
        {
          icon: "chevron-right",
          iconPack: "fa",
          type: "is-grey-icon",
          disabled: () => {
            return !this.canSeeDetails;
          },
          function: (row) => {
            this.$router.push({
              name: "Purchase",
              params: {
                id: row.id,
              },
            });
          },
        },
      ],
    };
  },
  computed: {
    canSeeDetails() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.PURCHASE.GET,
        AUTHORITIES.PURCHASE.ADMIN,
      ]);
    },
  },
  methods: {
    async fetch() {
      await this.$store.dispatch("purchase/fetchList", "sort=created,desc");
      this.purchases = this.$store.getters["purchase/getList"];
    },
  },
};
</script>
